import React from 'react';
import PropTypes from 'prop-types';
import './SafeHTMLRenderer.css';

/**
 * SafeHTMLRenderer
 * Renders structured or plain text content safely using React JSX (no dangerouslySetInnerHTML)
 *
 * Expected data formats:
 * Structured:
 * [
 *   {
 *     section: "Getting Started",
 *     items: [
 *       { title: "How do I start?", text: "Click the button to begin." },
 *       { title: "Do I need an account?", text: "Yes, to save progress." }
 *     ]
 *   },
 *   ...
 * ]
 *
 * Simple array:
 * [
 *   { title: "Question 1", text: "Answer 1" },
 *   { title: "Question 2", text: "Answer 2" }
 * ]
 *
 * Plain string:
 * "Welcome to our app! Here’s how it works..."
 */
const SafeHTMLRenderer = ({ content }) => {
  // Handle missing or invalid data gracefully
  if (!content)
    return <div className="safehtml-empty">No content available.</div>;

  // Case 1: Plain string
  if (typeof content === 'string') {
    return <p className="safehtml-text">{content}</p>;
  }

  // Case 2: Simple array or structured array
  if (Array.isArray(content)) {
    return (
      <div className="safehtml-container">
        {content.map((section, i) => {
          const items = section.items || section.details || [];

          // Case 2A: Structured section
          if (section.section || items.length > 0) {
            return (
              <div key={i} className="safehtml-section">
                {section.section && (
                  <h2 className="safehtml-section-title">{section.section}</h2>
                )}
                {items.map((item, j) => (
                  <div key={j} className="safehtml-item">
                    {item.title && (
                      <h3 className="safehtml-item-title">{item.title}</h3>
                    )}
                    {item.text && (
                      <p className="safehtml-item-text">{item.text}</p>
                    )}
                  </div>
                ))}
              </div>
            );
          }

          // Case 2B: Simple array of {title, text}
          return (
            <div key={i} className="safehtml-item">
              {section.title && (
                <h3 className="safehtml-item-title">{section.title}</h3>
              )}
              {section.text && (
                <p className="safehtml-item-text">{section.text}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Fallback
  return <div className="safehtml-empty">Unsupported content format.</div>;
};

SafeHTMLRenderer.propTypes = {
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        section: PropTypes.string,
        items: PropTypes.arrayOf(
          PropTypes.shape({
            title: PropTypes.string,
            text: PropTypes.string
          })
        )
      })
    )
  ])
};

export default SafeHTMLRenderer;
